/**
 * Inline error banner backed by a parsed `OcppApiErrorInfo`.
 *
 * Renders a coloured strip with the title + message and an optional retry
 * button. Use under (or above) the section content when the request to load
 * data failed.
 */

import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { OcppTheme } from './OcppTheme';
import type { OcppApiErrorInfo } from '../utils/apiErrors';

export interface OcppErrorBannerProps {
  error: OcppApiErrorInfo;
  onRetry?: () => void;
}

const colorForStatus = (status: number | undefined): string => {
  if (status === undefined) return OcppTheme.warning;
  if (status === 503) return OcppTheme.warning;
  if (status === 409) return OcppTheme.warning;
  if (status === 401 || status === 403 || status === 404) return OcppTheme.warning;
  return OcppTheme.danger;
};

const OcppErrorBanner: React.FC<OcppErrorBannerProps> = ({ error, onRetry }) => {
  const accent = colorForStatus(error.status);
  return (
    <View style={[styles.banner, { borderColor: accent, backgroundColor: `${accent}1A` }]}> 
      <MaterialIcons name="error-outline" size={20} color={accent} />
      <View style={styles.body}>
        <Text style={[styles.title, { color: accent }]}>{error.title}</Text>
        <Text style={styles.message}>{error.message}</Text>
      </View>
      {onRetry ? (
        <Pressable
          onPress={onRetry}
          style={({ pressed }) => [
            styles.retry,
            { borderColor: accent, opacity: pressed ? 0.7 : 1 },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Try again"
        >
          <Text style={[styles.retryText, { color: accent }]}>Retry</Text>
        </Pressable>
      ) : null}
    </View>
  );
};

export default OcppErrorBanner;

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: 10,
    padding: 12,
    gap: 10,
  },
  body: {
    flex: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 2,
  },
  message: {
    color: OcppTheme.textSecondary,
    fontSize: 13,
    lineHeight: 18,
  },
  retry: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    borderWidth: 1,
  },
  retryText: {
    fontSize: 12,
    fontWeight: '700',
  },
});
